import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileService } from '@api/service/file.service';
import { File as ApiFile } from '@api/model/file';
import { EcondosQuery } from '@api/model/query';
import { ProductBanner, ProductBannerCondoType, ProductBannerProduct, ProductBannerTopic } from '@api/model/product-banner';
import { ProductBannerService, SaveProductBannerDTO } from '@api/service/product-banner.service';
import { readFileAsDataURL } from '../../../../../util/read-file-as-data-url';

type SizeRange = { value: string; label: string; min: number; max: number | null };

/** Recorte do valor do formulário que alimenta o preview reativo. */
type BannerPreviewSource = Partial<Pick<ProductBanner, 'title' | 'description' | 'ctaLabel'>>;

@Component({
  selector: 'app-create-banner-page',
  templateUrl: './create-banner-page.component.html',
  styleUrls: ['./create-banner-page.component.scss']
})
export class CreateBannerPageComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  bannerId: string | null = null;
  isSaving = false;
  isUploadingPicture = false;

  topics: string[] = [];
  topicInput = '';
  uploadedPictureUrl = '';
  isDragOver = false;

  productTopics: ProductBannerTopic[] = [];

  readonly sizeRanges: SizeRange[] = [
    { value: 'ALL', label: 'Todos', min: 0, max: null },
    { value: 'UP_TO_50', label: 'Até 50', min: 0, max: 50 },
    { value: 'FROM_51_TO_200', label: '51 a 200', min: 51, max: 200 },
    { value: 'FROM_201_TO_500', label: '201 a 500', min: 201, max: 500 },
    { value: 'ABOVE_500', label: 'Acima de 500', min: 501, max: null }
  ];

  readonly condoTypeOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: 'HOUSE', label: 'Casa' },
    { value: 'APARTMENT', label: 'Apartamento' },
    { value: 'BUSINESS', label: 'Empresarial' },
    { value: 'OTHER', label: 'Outro' }
  ];

  // Preview reativo (evita função/getter em binding de leitura no template).
  preview = { title: '', description: '', ctaLabel: '' };

  bannerForm = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: Validators.required }),
    description: new FormControl('', { nonNullable: true }),
    ctaLabel: new FormControl('', { nonNullable: true }),
    ctaLink: new FormControl('', { nonNullable: true }),
    picture: new FormControl<ApiFile | null>(null),
    product: new FormControl<string | null>(null, Validators.required),
    profileOwner: new FormControl(true, { nonNullable: true }),
    profileAdmin: new FormControl(true, { nonNullable: true }),
    profileUser: new FormControl(false, { nonNullable: true }),
    profileGatekeeper: new FormControl(false, { nonNullable: true }),
    profileJanitor: new FormControl(false, { nonNullable: true }),
    position: new FormControl(1, { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    isActive: new FormControl(true, { nonNullable: true }),
    condoType: new FormControl('ALL', { nonNullable: true }),
    sizeRange: new FormControl('ALL', { nonNullable: true })
  });

  constructor(
    private productBannerService: ProductBannerService,
    private fileService: FileService,
    private router: Router,
    private route: ActivatedRoute,
    private notification: NzNotificationService
  ) {}

  ngOnInit(): void {
    this.productBannerService
      .getTopics()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: topics => (this.productTopics = topics),
        error: () => this.notification.create('error', 'Erro', 'Não foi possível carregar os produtos.')
      });

    this.updatePreview(this.bannerForm.value);
    this.bannerForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => this.updatePreview(value));

    this.bannerId = this.route.snapshot.paramMap.get('bannerId');
    if (this.bannerId) {
      this.loadBanner(this.bannerId);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private updatePreview(value: BannerPreviewSource): void {
    this.preview = { title: value.title || '', description: value.description || '', ctaLabel: value.ctaLabel || '' };
  }

  private loadBanner(bannerId: string): void {
    // $select é obrigatório: a API v2 força $select='_id' quando ausente e o banner viria vazio.
    const query: EcondosQuery = {
      $select: 'title description topics ctaLabel ctaLink picture product condoTypes condoSizeMin condoSizeMax userTypes position isActive',
      $populate: [{ path: 'picture', select: 'url thumbnail type format name' }]
    };
    this.productBannerService
      .getProductBannerById(bannerId, query)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: banner => this.patchForm(banner),
        error: () => this.notification.create('error', 'Erro', 'Não foi possível carregar o banner.')
      });
  }

  private patchForm(banner: ProductBanner): void {
    this.topics = banner.topics || [];
    this.uploadedPictureUrl = banner.picture?.url || '';
    this.bannerForm.patchValue({
      title: banner.title,
      description: banner.description,
      ctaLabel: banner.ctaLabel,
      ctaLink: banner.ctaLink,
      picture: banner.picture || null,
      product: banner.product,
      profileOwner: (banner.userTypes || []).includes('OWNER'),
      profileAdmin: (banner.userTypes || []).includes('ADMIN'),
      profileUser: (banner.userTypes || []).includes('USER'),
      profileGatekeeper: (banner.userTypes || []).includes('GATEKEEPER'),
      profileJanitor: (banner.userTypes || []).includes('JANITOR'),
      position: banner.position,
      isActive: banner.isActive,
      condoType: banner.condoTypes && banner.condoTypes.length ? banner.condoTypes[0] : 'ALL',
      sizeRange: this.matchSizeRange(banner.condoSizeMin, banner.condoSizeMax)
    });
  }

  private matchSizeRange(min?: number, max?: number | null): string {
    const found = this.sizeRanges.find(range => range.min === (min ?? 0) && (range.max ?? null) === (max ?? null));
    return found ? found.value : 'ALL';
  }

  addTopic(): void {
    const value = this.topicInput.trim();
    if (value) {
      this.topics = [...this.topics, value];
      this.topicInput = '';
    }
  }

  removeTopic(index: number): void {
    this.topics = this.topics.filter((_, i) => i !== index);
  }

  onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length) {
      this.handleUploadPicture(files.item(0) as File);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.handleUploadPicture(files.item(0) as File);
    }
  }

  private handleUploadPicture(file: File): void {
    this.isUploadingPicture = true;
    const formData = new FormData();
    formData.append(file.name || 'file', file);

    this.fileService
      .uploadFilesFromFormData(formData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async ([uploadedPicture]) => {
          this.bannerForm.controls.picture.setValue(uploadedPicture);
          this.uploadedPictureUrl = await readFileAsDataURL(file);
          this.isUploadingPicture = false;
        },
        error: () => {
          this.isUploadingPicture = false;
          this.notification.create('error', 'Erro', 'Não foi possível enviar a imagem.');
        }
      });
  }

  removePicture(): void {
    this.bannerForm.controls.picture.setValue(null);
    this.uploadedPictureUrl = '';
  }

  cancel(): void {
    this.router.navigate(['/home/marketplace/banners']);
  }

  save(): void {
    if (this.isSaving) {
      return;
    }
    if (this.bannerForm.invalid) {
      this.bannerForm.markAllAsTouched();
      this.notification.create('warning', 'Formulário inválido', 'Preencha os campos obrigatórios (título e produto).');
      return;
    }

    this.isSaving = true;
    const value = this.bannerForm.getRawValue();
    const range = this.sizeRanges.find(r => r.value === value.sizeRange) || this.sizeRanges[0];

    const userTypes: SaveProductBannerDTO['userTypes'] = [];
    if (value.profileOwner) {
      userTypes.push('OWNER');
    }
    if (value.profileAdmin) {
      userTypes.push('ADMIN');
    }
    if (value.profileUser) {
      userTypes.push('USER');
    }
    if (value.profileGatekeeper) {
      userTypes.push('GATEKEEPER');
    }
    if (value.profileJanitor) {
      userTypes.push('JANITOR');
    }

    const data: SaveProductBannerDTO = {
      title: value.title,
      description: value.description,
      topics: this.topics,
      ctaLabel: value.ctaLabel,
      ctaLink: value.ctaLink,
      picture: value.picture?._id || null,
      product: value.product as ProductBannerProduct,
      // Seleção única no form (Figma): 'ALL' vira [] (todos); senão, um único tipo.
      condoTypes: value.condoType === 'ALL' ? [] : [value.condoType as ProductBannerCondoType],
      condoSizeMin: range.min,
      // Faixa sem limite superior vai como null explícito: `undefined` some do JSON e o
      // PUT parcial do backend manteria o condoSizeMax gravado antes.
      condoSizeMax: range.max,
      userTypes,
      position: value.position,
      isActive: value.isActive
    };

    const request = this.bannerId
      ? this.productBannerService.updateProductBanner(this.bannerId, data)
      : this.productBannerService.createProductBanner(data);

    request.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.notification.create('success', 'Banner', 'Banner salvo com sucesso.');
        this.router.navigate(['/home/marketplace/banners']);
      },
      error: err => {
        this.isSaving = false;
        this.notification.create('error', 'Erro', err?.error?.message || 'Não foi possível salvar o banner.');
      }
    });
  }
}
